"use client";

import { FC, HTMLAttributes, ReactNode, useEffect, useState } from "react";
import Image from "next/image";
import clsx from "clsx";
import { Accordion } from "@mantine/core";

// components
import TButton from "@/components/atoms/buttons/TButton";
import icon from "@/utils/icon.helper";
import HtmlText from "@/sections/common/HtmlText";

//types
import { ITextButton } from "@/components/atoms/buttons/types";
import { IIconName } from "@/utils/icon.helper";

import PlusIcon from "@/assets/images/icons/plus.svg";
import { Carousel } from "@mantine/carousel";

import { useViewportSize } from "@mantine/hooks";
import Process from "./Process";

// ------------------------------------------------------------------------------------------------

export type IProcess = {
  icon?: IIconName;
  heading?: string;
  description?: ReactNode;
  button?: ITextButton[];
};

export type IProcessSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  processes?: IProcess[];
};

// --------------------------------------------------------------------------------------------------

const ProcessSection: FC<IProcessSectionProps> = (props) => {
  const { heading, processes, className, ...other } = props;

  const [activeValue, setActiveValue] = useState<number>(0);

  const [activeAccordion, setActiveAccordion] = useState<string | undefined>(
    processes
      ? processes[processes.findIndex((item) => !!item.heading === true)]
          .heading
      : "",
  );
  const [isHovered, setIsHovered] = useState(false);

  const { width } = useViewportSize();

  const processHeadings = processes
    ? processes.map((item) => ({ heading: item.heading }))
    : [];

  // this is for Carousel
  useEffect(() => {
    if (width >= 1024) {
      const autoAnimation = setInterval(() => {
        processes && setActiveValue((activeValue + 1) % processes.length);
      }, 3000);

      if (isHovered) clearInterval(autoAnimation);

      return () => {
        clearInterval(autoAnimation);
      };
    }
  }, [activeValue, isHovered, processes, width]);

  return (
    <section
      className={clsx("container py-5 lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      {/* Desktop */}
      <div className="hidden items-start gap-6 lg:flex">
        <div className="w-full max-w-[350px] xl:max-w-[415px]">
          <h2 className="mb-10 text-h5 font-medium text-black-900 lg:text-h3 xl:mb-14 xl:text-h2">
            {heading}
          </h2>

          <Carousel
            initialSlide={activeValue}
            draggable={false}
            withControls={false}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {processes?.map((process, idx) => (
              <Carousel.Slide key={idx} className="pl-1">
                <div className="size-8 lg:size-10 xl:size-[50px]">
                  {process?.icon && (
                    <Image
                      src={icon(process.icon)}
                      alt={process.icon}
                      height={50}
                      width={50}
                      className="size-full"
                    />
                  )}
                </div>

                <h3 className="text-h6 font-normal text-black-900 lg:my-7 lg:text-h5 xl:text-h4">
                  {process.heading}
                </h3>

                {process.description && (
                  <HtmlText>{process.description}</HtmlText>
                )}

                {process.button?.map((btn, idx) => (
                  <TButton
                    key={idx}
                    id={btn.id}
                    variant={btn.variant}
                    href={btn.link}
                    target={btn.target}
                    radius={btn.radius}
                    className="mt-5 lg:mt-7"
                  >
                    {btn.text}
                  </TButton>
                ))}
              </Carousel.Slide>
            ))}
          </Carousel>
        </div>

        <Process
          points={processHeadings}
          activePoint={activeValue}
          setActivePoint={setActiveValue}
        />
      </div>

      {heading && (
        <h2 className="mb-8 text-h5 font-medium text-black-900 lg:hidden lg:text-h3 xl:text-h2">
          {heading}
        </h2>
      )}
      {processes && processes.length > 0 && (
        <Accordion
          className="lg:hidden"
          defaultValue={
            processes[processes.findIndex((item) => !!item.heading === true)]
              .heading
          }
          classNames={{
            label: "pr-2",
            chevron: "size-5 data-[rotate]:rotate-45",
            control: "items-baseline px-4",
            content: "pl-9",
          }}
          chevron={
            <Image src={PlusIcon.src} alt="plus icon" height={20} width={20} />
          }
          onChange={(value) => value !== null && setActiveAccordion(value)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {processes.map((process, idx) => (
            <Accordion.Item
              key={idx}
              value={process.heading ? process.heading : ""}
            >
              <Accordion.Control
                className="p-0"
                onClick={() =>
                  process.heading === activeAccordion
                    ? setActiveAccordion("")
                    : setActiveAccordion(process.heading)
                }
              >
                <h4 className="flex w-fit flex-row items-center py-3 text-h6 font-normal text-black-900 lg:text-h5 xl:text-h4">
                  {process.icon && (
                    <div className="mr-4 h-8 w-8">
                      {" "}
                      <Image
                        src={icon(process.icon)}
                        alt={process.icon}
                        className="size-full"
                        height={32}
                        width={32}
                      />{" "}
                    </div>
                  )}
                  {process.heading}
                </h4>
              </Accordion.Control>

              <Accordion.Panel className="pl-5 md:pl-7">
                {process.description && (
                  <div className="text-base text-gray-500">
                    {process.description}
                  </div>
                )}

                {process.button?.map((btn, idx) => (
                  <TButton
                    key={idx}
                    id={btn.id}
                    variant={btn.variant}
                    href={btn.link}
                    target={btn.target}
                    radius={btn.radius}
                    className="mt-5 lg:mt-7"
                  >
                    {btn.text}
                  </TButton>
                ))}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </section>
  );
};

export default ProcessSection;
